import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CalendarResponse, CreateCalendarEventRequest } from '@tabita/contracts';
import { useI18n } from '@/i18n/i18n-provider';
import { calendarApi } from '@/services/calendar.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Spinner, Switch } from '@/components/ui';

export type CalendarEventDialogMode =
  { readonly mode: 'create' } | { readonly mode: 'edit'; readonly eventId: string };

function errorMessage(
  error: unknown,
  t: (key: string, options?: { fallback?: string }) => string,
): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}

/** Splits an ISO `startAt`/`endAt` into the separate `date`/`time` inputs
 *  the form uses — both read as literal wall-clock values, no timezone
 *  conversion, matching the backend's own UTC-as-wall-clock simplification. */
function splitIso(iso: string): { date: string; time: string } {
  const parts = iso.split('T');
  return { date: parts[0] ?? '', time: (parts[1] ?? '00:00').slice(0, 5) };
}

function combineIso(date: string, time: string): string {
  return `${date}T${time}:00.000Z`;
}

export function CalendarEventDialog({
  calendars,
  mode,
  onClose,
}: {
  readonly calendars: readonly CalendarResponse[];
  readonly mode: CalendarEventDialogMode;
  readonly onClose: () => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(mode.mode === 'create');

  const [calendarId, setCalendarId] = useState(calendars[0]?.id ?? '');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('09:00');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('10:00');
  const [repeatsWeekly, setRepeatsWeekly] = useState(false);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const eventQuery = useQuery({
    queryKey: ['calendar', 'events', mode.mode === 'edit' ? mode.eventId : null],
    queryFn: () => calendarApi.getEvent(mode.mode === 'edit' ? mode.eventId : ''),
    enabled: mode.mode === 'edit',
  });

  useEffect(() => {
    if (mode.mode !== 'edit' || !eventQuery.data || initialized) return;
    const event = eventQuery.data;
    const start = splitIso(event.startAt);
    const end = splitIso(event.endAt);
    setCalendarId(event.calendarId);
    setTitle(event.title);
    setDescription(event.description ?? '');
    setLocation(event.location ?? '');
    setIsAllDay(event.isAllDay);
    setStartDate(start.date);
    setStartTime(start.time);
    setEndDate(end.date);
    setEndTime(end.time);
    setRepeatsWeekly(event.recurrenceFrequency === 'WEEKLY');
    setRecurrenceEndDate(event.recurrenceEndDate ?? '');
    setInitialized(true);
  }, [mode, eventQuery.data, initialized]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'occurrences'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar', 'events'] });
  };

  const buildRequest = (): CreateCalendarEventRequest => ({
    calendarId,
    title,
    description: description.trim() || null,
    location: location.trim() || null,
    isAllDay,
    startAt: combineIso(startDate, isAllDay ? '00:00' : startTime),
    endAt: combineIso(endDate || startDate, isAllDay ? '23:59' : endTime),
    recurrenceFrequency: repeatsWeekly ? 'WEEKLY' : 'NONE',
    recurrenceEndDate: repeatsWeekly && recurrenceEndDate ? recurrenceEndDate : null,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      mode.mode === 'edit'
        ? calendarApi.updateEvent(mode.eventId, buildRequest())
        : calendarApi.createEvent(buildRequest()),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, t)),
  });

  const deleteMutation = useMutation({
    mutationFn: () => calendarApi.deleteEvent(mode.mode === 'edit' ? mode.eventId : ''),
    onSuccess: () => {
      invalidate();
      onClose();
    },
    onError: (error: unknown) => setFormError(errorMessage(error, t)),
  });

  const loading = mode.mode === 'edit' && !initialized;
  const canSubmit = Boolean(
    calendarId && title.trim() && startDate && (isAllDay || endDate || startDate),
  );

  return (
    <dialog
      ref={dialogRef}
      onClose={onClose}
      className="dialog calendar-event-dialog"
      aria-labelledby="calendar-event-title"
    >
      {loading ? (
        <div className="dialog-body">
          <Spinner label={t('common.states.loading')} />
        </div>
      ) : (
        <form
          className="dialog-body"
          onSubmit={(event) => {
            event.preventDefault();
            setFormError(null);
            saveMutation.mutate();
          }}
          noValidate
        >
          <h2 id="calendar-event-title">
            {mode.mode === 'edit' ? t('calendar.event.edit') : t('calendar.event.add')}
          </h2>

          {formError && <Alert tone="danger">{formError}</Alert>}

          <Field
            label={t('calendar.event.name')}
            name="title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            required
            autoFocus
          />

          <div className="field">
            <label className="field-label" htmlFor="calendar-event-calendar">
              {t('calendar.calendar.title')}
            </label>
            <select
              id="calendar-event-calendar"
              className="field-input"
              value={calendarId}
              onChange={(event) => setCalendarId(event.target.value)}
              required
            >
              <option value="" />
              {calendars.map((calendar) => (
                <option key={calendar.id} value={calendar.id}>
                  {calendar.name}
                </option>
              ))}
            </select>
          </div>

          <Switch checked={isAllDay} onChange={setIsAllDay} label={t('calendar.event.allDay')} />

          <div className="calendar-event-datetime-row">
            <Field
              label={t('calendar.event.start')}
              name="startDate"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              required
            />
            {!isAllDay && (
              <Field
                label={t('calendar.event.startTime')}
                name="startTime"
                type="time"
                value={startTime}
                onChange={(event) => setStartTime(event.target.value)}
                required
              />
            )}
          </div>

          <div className="calendar-event-datetime-row">
            <Field
              label={t('calendar.event.end')}
              name="endDate"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
            {!isAllDay && (
              <Field
                label={t('calendar.event.endTime')}
                name="endTime"
                type="time"
                value={endTime}
                onChange={(event) => setEndTime(event.target.value)}
              />
            )}
          </div>

          <Field
            label={t('calendar.event.location')}
            name="location"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />

          <div className="field">
            <label className="field-label" htmlFor="calendar-event-description">
              {t('calendar.event.description')}
            </label>
            <textarea
              id="calendar-event-description"
              className="field-input"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>

          <Switch
            checked={repeatsWeekly}
            onChange={setRepeatsWeekly}
            label={t('calendar.event.repeatsWeekly')}
          />

          {repeatsWeekly && (
            <Field
              label={t('calendar.event.repeatsUntil')}
              name="recurrenceEndDate"
              type="date"
              value={recurrenceEndDate}
              onChange={(event) => setRecurrenceEndDate(event.target.value)}
              hint={t('calendar.event.repeatsUntilHint')}
            />
          )}

          <footer className="dialog-actions">
            {mode.mode === 'edit' && (
              <Button
                type="button"
                variant="danger"
                className="calendar-event-delete"
                onClick={() => {
                  setFormError(null);
                  deleteMutation.mutate();
                }}
                loading={deleteMutation.isPending}
              >
                {t('common.actions.delete')}
              </Button>
            )}
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!canSubmit}
              loading={saveMutation.isPending}
            >
              {t('common.actions.save')}
            </Button>
          </footer>
        </form>
      )}
    </dialog>
  );
}
